import React, { useState, useEffect, useRef } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { ArrowLeft, Send, Bot, User } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Chat = () => {
  const { token, user } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    const welcomeMessage = {
      role: 'assistant',
      content: t('chatWelcome')
    };
    setMessages([welcomeMessage]);
  }, [language]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage = { role: 'user', content: input };
    setMessages([...messages, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await axios.post(
        `${API}/chat`,
        { message: input, user_id: user?.id || 'guest' },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const assistantMessage = { role: 'assistant', content: response.data.response };
      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      toast.error(t('sendError'));
      console.error('Chat error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <div className="min-h-screen bg-sand flex flex-col">
      {/* Header */}
      <nav className="bg-white border-b-2 border-stone-200 shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate('/dashboard')}
            data-testid="back-to-dashboard"
            className="tile-button bg-stone-200 text-charcoal p-2 rounded-lg hover:bg-stone-300"
          >
            <ArrowLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
          <h1 className="font-playfair text-2xl sm:text-3xl font-bold text-primary" data-testid="chat-title">
            {t('chatTitle')}
          </h1>
        </div>
      </nav>

      {/* Chat Messages */}
      <div className="flex-1 container mx-auto px-4 sm:px-6 py-6 sm:py-8 max-w-4xl flex flex-col">
        <div className="flex-1 overflow-y-auto mb-4 sm:mb-6 space-y-4" data-testid="chat-messages">
          {messages.map((msg, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[85%] sm:max-w-[80%] rounded-2xl p-3 sm:p-4 ${
                  msg.role === 'user'
                    ? 'bg-primary text-white shadow-lg'
                    : 'bg-white border-2 border-stone-200 text-charcoal shadow-sm'
                }`}
                data-testid={`message-${index}`}
              >
                <div className="flex items-start gap-2 sm:gap-3">
                  <div
                    className={`p-1.5 sm:p-2 rounded-lg flex-shrink-0 ${
                      msg.role === 'user' ? 'bg-white/20' : 'bg-oasis/10'
                    }`}
                  >
                    {msg.role === 'user' ? (
                      <User className="w-4 h-4 sm:w-5 sm:h-5" />
                    ) : (
                      <Bot className="w-4 h-4 sm:w-5 sm:h-5 text-oasis" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-nunito text-sm sm:text-base leading-relaxed whitespace-pre-wrap break-words">
                      {msg.content}
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
          
          {loading && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="flex justify-start"
            >
              <div className="bg-white border-2 border-stone-200 rounded-2xl p-4 shadow-sm">
                <div className="flex items-center gap-2">
                  <Bot className="w-5 h-5 text-oasis" />
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-oasis rounded-full animate-bounce" style={{ animationDelay: '0s' }} />
                    <div className="w-2 h-2 bg-oasis rounded-full animate-bounce" style={{ animationDelay: '0.2s' }} />
                    <div className="w-2 h-2 bg-oasis rounded-full animate-bounce" style={{ animationDelay: '0.4s' }} />
                  </div>
                </div>
              </div>
            </motion.div>
          )}
          
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div className="bg-white rounded-2xl shadow-xl border-2 border-stone-200 p-3 sm:p-4">
          <div className="flex gap-2 sm:gap-3">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder={t('chatPlaceholder')}
              disabled={loading}
              data-testid="chat-input"
              className="flex-1 bg-sand border-2 border-stone-300 text-charcoal rounded-lg focus:border-primary focus:ring-0 px-3 sm:px-4 py-2.5 sm:py-3 font-nunito text-sm sm:text-base"
            />
            <button
              onClick={handleSend}
              disabled={loading || !input.trim()}
              data-testid="send-message-btn"
              className="tile-button bg-primary text-white font-nunito font-bold py-2.5 sm:py-3 px-4 sm:px-6 rounded-lg shadow-[0_4px_0_#9a360b] active:shadow-none disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Send className="w-4 h-4 sm:w-5 sm:h-5" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Chat;
